import React, { useState } from "react";
import { db, storage } from "../../firebaseConfig"; // Ensure firebaseConfig is properly imported
import { addDoc, collection, updateDoc, Timestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const AddCarForm = ({ onClose }) => {
  const [carData, setCarData] = useState({
    carName: "",
    year: "",
    mileage: "",
    vin: "",                      // ADDED
    totalValue: "",
    minimumInvestment: "",
    freeDays: "",
    freePercent: "",
    predictedROI: "",
    "12month": "",               // ADDED
    "24month": "",               // ADDED
    risk: "",                    // ADDED
    maintenanceHistory: "",      // ADDED
    tags: [],
    carType: [],
    location: "",
    summary: "",
    businessStrategy: "",
    maintenanceCost: "",
    insuranceCost: "",
    isAvailable: true,
  });

  const [documents, setDocuments] = useState({
    insurance: { file: null, expiry: null, uploaded: false },
    registration: { file: null, expiry: null, uploaded: false },
    LLC: { file: null, expiry: null, uploaded: false },
  });

  const [pictures, setPictures] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCarData({ ...carData, [name]: value });
  };

  const handleCheckboxChange = (name, value) => {
    setCarData((prev) => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter((v) => v !== value)
        : [...prev[name], value],
    }));
  };

  const handleFileUpload = async (file, fieldName) => {
    const fileRef = ref(storage, `cars/${carData.carName}/${fieldName}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => console.error("Upload failed:", error),
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setDocuments((prev) => ({
          ...prev,
          [fieldName]: { ...prev[fieldName], file: url, uploaded: true },
        }));
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Upload pictures and get their download URLs
      const pictureURLs = [];
      for (const picture of pictures) {
        const pictureRef = ref(
          storage,
          `cars/${carData.carName}/${picture.name}`
        );
        const uploadTask = await uploadBytesResumable(pictureRef, picture);
        const downloadURL = await getDownloadURL(uploadTask.ref);
        pictureURLs.push(downloadURL);
      }

      // Prepare documents that have actually been uploaded
      const uploadedDocuments = {};
      for (const [docKey, docValue] of Object.entries(documents)) {
        if (docValue.file && docValue.uploaded) {
          uploadedDocuments[docKey] = {
            file: docValue.file,
            expiry: docValue.expiry,
          };
        }
      }

      // Create the car document in Firestore (without carID initially)
      const carDocRef = await addDoc(collection(db, "cars"), {
        ...carData,
        freeDaysRatio: `${carData.freeDays} days per ${carData.freePercent}%`,
        tags: carData.tags,
        carType: carData.carType,
        pictures: pictureURLs,
        documents: uploadedDocuments,
        createdAt: Timestamp.now(),
      });

      // Update the document to include the carID
      await updateDoc(carDocRef, { carID: carDocRef.id });

      console.log("Car added successfully with ID:", carDocRef.id);
      alert("Car added successfully!");
      onClose(); // Close the form if provided
    } catch (error) {
      console.error("Error adding car:", error);
      alert("Failed to add car. Please try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
      <h2>Add New Car</h2>
      {/* Basic Info */}
      <div>
        <label>Car Name:</label>
        <input
          type="text"
          name="carName"
          value={carData.carName}
          onChange={handleInputChange}
          required
        />
      </div>
      <div>
        <label>Year:</label>
        <input
          type="number"
          name="year"
          value={carData.year}
          onChange={handleInputChange}
          required
        />
      </div>
      <div>
        <label>Mileage:</label>
        <input
          type="number"
          name="mileage"
          value={carData.mileage}
          onChange={handleInputChange}
          required
        />
      </div>
      <div>
        <label>VIN:</label>
        <input
          type="text"
          name="vin"
          value={carData.vin}
          onChange={handleInputChange}
          required
        />
      </div>
      <div>
        <label>Total Value / Offering Price:</label>
        <input
          type="number"
          name="totalValue"
          value={carData.totalValue}
          onChange={handleInputChange}
          required
        />
      </div>
      <div>
        <label>Minimum Investment:</label>
        <input
          type="number"
          name="minimumInvestment"
          value={carData.minimumInvestment}
          onChange={handleInputChange}
          required
        />
      </div>
      <div>
        <label>Free Days Ratio:</label>
        <div>
          <input
            type="number"
            name="freeDays"
            value={carData.freeDays}
            onChange={handleInputChange}
            placeholder="Days"
            required
          />
          per
          <input
            type="number"
            name="freePercent"
            value={carData.freePercent}
            onChange={handleInputChange}
            placeholder="Percent"
            required
          />
        </div>
      </div>
      <div>
        <label>Predicted ROI:</label>
        <input
          type="number"
          name="predictedROI"
          value={carData.predictedROI}
          onChange={handleInputChange}
          required
        />
      </div>

      {/* Additional ROI Fields */}
      <div>
        <label>12-Month Projected ROI:</label>
        <input
          type="number"
          name="12month"
          value={carData["12month"]}
          onChange={handleInputChange}
        />
      </div>
      <div>
        <label>24-Month Projected ROI:</label>
        <input
          type="number"
          name="24month"
          value={carData["24month"]}
          onChange={handleInputChange}
        />
      </div>
      <div>
        <label>Risk Level:</label>
        <input
          type="text"
          name="risk"
          value={carData.risk}
          onChange={handleInputChange}
        />
      </div>
      <div>
        <label>Maintenance History:</label>
        <textarea
          name="maintenanceHistory"
          value={carData.maintenanceHistory}
          onChange={handleInputChange}
          rows={3}
        />
      </div>

      {/* Tags */}
      <div>
        <label>Tags:</label>
        <div>
          {["One of One", "Rare", "Limited", "Cash Flow"].map((tag) => (
            <label key={tag} style={{ marginRight: "10px" }}>
              <input
                type="checkbox"
                onChange={() => handleCheckboxChange("tags", tag)}
                checked={carData.tags.includes(tag)}
              />
              {tag}
            </label>
          ))}
        </div>
      </div>

      {/* Car Type */}
      <div>
        <label>Car Type:</label>
        <div>
          {["Rent", "Flip", "Both"].map((type) => (
            <label key={type} style={{ marginRight: "10px" }}>
              <input
                type="checkbox"
                onChange={() => handleCheckboxChange("carType", type)}
                checked={carData.carType.includes(type)}
              />
              {type}
            </label>
          ))}
        </div>
      </div>

      {/* Location & Summaries */}
      <div>
        <label>Location:</label>
        <input
          type="text"
          name="location"
          value={carData.location}
          onChange={handleInputChange}
          required
        />
      </div>
      <div>
        <label>Summary:</label>
        <textarea
          name="summary"
          value={carData.summary}
          onChange={handleInputChange}
          rows={3}
        />
      </div>
      <div>
        <label>Business Strategy:</label>
        <textarea
          name="businessStrategy"
          value={carData.businessStrategy}
          onChange={handleInputChange}
          rows={3}
        />
      </div>

      {/* Maintenance & Insurance */}
      <div>
        <label>Maintenance Cost:</label>
        <input
          type="number"
          name="maintenanceCost"
          value={carData.maintenanceCost}
          onChange={handleInputChange}
        />
      </div>
      <div>
        <label>Insurance Cost:</label>
        <input
          type="number"
          name="insuranceCost"
          value={carData.insuranceCost}
          onChange={handleInputChange}
        />
      </div>

      {/* Pictures */}
      <div>
        <label>Pictures:</label>
        <input
          type="file"
          multiple
          onChange={(e) => setPictures([...e.target.files])}
        />
      </div>

      {/* Documents */}
      <div>
        <label>Documents:</label>
        {["insurance", "registration", "LLC"].map((doc) => (
          <div key={doc} style={{ marginBottom: "10px" }}>
            <span style={{ textTransform: "capitalize" }}>{doc}:</span>
            <input
              type="file"
              onChange={(e) =>
                setDocuments((prev) => ({
                  ...prev,
                  [doc]: { ...prev[doc], file: e.target.files[0] },
                }))
              }
              style={{ marginLeft: "10px" }}
            />
            <input
              type="date"
              onChange={(e) =>
                setDocuments((prev) => ({
                  ...prev,
                  [doc]: { ...prev[doc], expiry: e.target.value },
                }))
              }
              style={{ marginLeft: "10px" }}
            />
          </div>
        ))}
      </div>

      <button type="submit" style={{ marginTop: "20px" }}>
        Add Car
      </button>
    </form>
  );
};

export default AddCarForm;
